<template>
  <div class="my-2">
    <FloatLabel variant="on">
      <MultiSelect
        v-model="selectedValue"
        :options="mappedOptions"
        :option-label="optionLabel"
        :option-value="optionValue"
        :option-group-label="optionGroupLabel"
        :option-group-children="optionGroupChildren"
        size="large"
        :disabled="disabled"
        :invalid="!!error"
        :filter="useFilter"
        :max-selected-labels="2"
        :selected-items-label="selectedLabel"
        class="w-100"
        @change="onChange"
        @filter="onFilter"
      >
        <template #empty>
          <div>گزینه‌ای وجود ندارد</div>
        </template>

        <template #emptyfilter>
          <div>نتیجه‌ای یافت نشد</div>
        </template>
      </MultiSelect>
      <label>{{ placeholder }}</label>
    </FloatLabel>

    <small
      v-if="error"
      class="font-size-sm text-red mt-1 d-block"
    >
      {{ error }}
    </small>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import MultiSelect from 'primevue/multiselect';
import FloatLabel from 'primevue/floatlabel';
import { getCurrentInstance } from "vue";
import UIMixin from "@/mixins/UIMixin";

const vm = getCurrentInstance().proxy;
const toPersianDigits = (...args) => UIMixin.methods.toPersianDigits.call(vm, ...args);

const props = defineProps({
  modelValue: {
    type: [Array, null],
    default: () => [],
  },
  placeholder: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  optionLabel: {
    type: String,
    default: "text",
  },  
  optionValue: {
    type: String,
    default: "id",
  },
  optionGroupLabel: {
    type: String,
    default: null,
  },
  optionGroupChildren: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'onChange']);

const computedVirtualScrollerOptions = computed(() => {
  return props.options.length > 10 ? virtualScrollerOptions : undefined;
});

const virtualScrollerOptions = {
  itemSize: 38,
  showSpacer: true,
  delay: 0,
  lazy: true,
  step: 10,
  orientation: 'vertical',
}

// token to represent real null ids inside PrimeVue multiselect
const NULL_TOKEN = '__SOHA_NULL__'

// map options so option.id === null becomes token (PrimeVue treats null as "no selection")
const mappedOptions = computed(() => {
  return (props.options || []).map(o => {
    if (o && o.id === null) return { ...o, id: NULL_TOKEN }
    return o
  })
})

const selectedLabel = computed(() => {
  let oneSelectTitle = props.options.find(o => o.id == selectedValue.value[0])?.text;
  let otherCount = selectedValue.value.length - 1;

  return `${oneSelectTitle} و ${toPersianDigits(otherCount)} مورد دیگر`;
})

// enable internal filter when many options
const useFilter = computed(() => (mappedOptions.value || []).length > 5)

// helpers to normalize arrays
const normalizeItem = (val) => {
  if (val == null) {
    return mappedOptions.value.some(o => o.id === NULL_TOKEN) ? NULL_TOKEN : null
  }
  if (typeof val === 'object' && val !== null && 'id' in val) {
    return val.id === null ? NULL_TOKEN : val.id
  }

  if (!props.options || props.options.length === 0) return val

  // try direct find
  let found = props.options.find(o => o.id === val)
  if (found) return found.id === null ? NULL_TOKEN : found.id

  const num = Number(val)
  if (!Number.isNaN(num)) {
    found = props.options.find(o => o.id === num)
    if (found) return found.id === null ? NULL_TOKEN : found.id
  }

  const str = String(val)
  found = props.options.find(o => String(o.id) === str)
  if (found) return found.id === null ? NULL_TOKEN : found.id

  return val
}

const normalizeArray = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.map(a => normalizeItem(a)).filter(v => v !== undefined && v !== null ? true : (v === NULL_TOKEN))
}

const selectedValue = ref([])

// sync incoming modelValue -> internal selectedValue
watch(() => props.modelValue, (newVal) => {
  selectedValue.value = normalizeArray(newVal || [])
}, { immediate: true })

// re-normalize when options change (async arrival)
watch(() => props.options, () => {
  selectedValue.value = normalizeArray(props.modelValue || [])
}, { deep: true, immediate: true })

// when internal selectedValue changes emit update:modelValue (map token back to null)
watch(selectedValue, (newVal) => {
  // map token back to actual null
  const out = (newVal || []).map(v => v === NULL_TOKEN ? null : v);

  // normalize prev from prop to comparable form (ensure array)
  const prev = Array.isArray(props.modelValue) ? props.modelValue : [];

  // shallow compare arrays (order sensitive). اگر لازم باشه مقایسه عمیق‌تری بذار
  const isSame =
    prev.length === out.length &&
    prev.every((p, i) => {
      // treat null and undefined equivalently if لازم باشه
      if (p === out[i]) return true;
      // string/number lenient compare
      return String(p) === String(out[i]);
    });

  if (!isSame) {
    emit('update:modelValue', out);
  }
})

// onChange: emit original option objects array (with null id when appropriate)
const onChange = (event) => {
  const vals = Array.isArray(event?.value) ? event.value : []
  const selectedOptions = (props.options || []).filter(option => {
    if (!option) return false
    if (option.id === null && vals.includes(NULL_TOKEN)) return true
    return vals.some(v => String(v) === String(option.id))
  })
  emit('onChange', selectedOptions)
}

const onFilter = () => {}
</script>

